import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import EntityListView from '../listView/EntityListView';
import showConfirmToast from '../businessUtilities/ConfirmToast';
import useDebounce from '../hooks/useDebounce';
import EditEntityModal from '../../common/commonUtils/EditEntityModal';
import { useAuth } from '../../../context/AuthProvider';
import Filters from '../businessUtilities/Filters';
import { useIsAdmin } from '../hooks/useIsAdmin';
import AddEntityModal from '../businessUtilities/AddEntityModal';
import { CustomersApi } from '../../../api/common/apiIndex';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';
import Loader from '../../common/commonUtils/Loader';

export default function Customers() {
  const queryClient = useQueryClient();
  const { country: userCountry } = useAuth();
  const isAdmin = useIsAdmin();
  const isDark = useIsDarkTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [addingEntity, setAddingEntity] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const entityKey = 'customers';
  const pageSize = 10;
  const sort = 'createdAt,desc';
  const debounceMs = 350;
  const emptyMessage = 'No customer found.';
  const deleteConfirmMessage = 'Are you sure you want to delete this customer?';
  const deleteSuccessToast = 'Customer deleted.';
  const updateSuccessToast = 'Customer updated successfully';
  const editTitle = 'Edit';
  const getCountry = (r) => r.country;
  const getEditLabel = (r) => r.firstName || r.name || `#${r.id}`;

  const q = useDebounce(searchTerm, debounceMs);
  const effectiveCountry = useMemo(
    () => (isAdmin ? country : (userCountry || '')),
    [isAdmin, country, userCountry]
  );

  useEffect(() => {
    setPage(0);
  }, [q, effectiveCountry]);

  const {
    data: pageData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [entityKey, { page, size: pageSize, sort, q, country: effectiveCountry }],
    queryFn: () => CustomersApi.list({ page, size: pageSize, sort, q, country: effectiveCountry }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 10,
    placeholderData: (prev) => prev,
  });

  const rows = pageData?.content ?? [];
  const totalPages = pageData?.totalPages ?? 0;
  const totalElements = pageData?.totalElements ?? 0;
  const deleteMutation = useMutation({
    mutationFn: (id) => CustomersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [entityKey] });
      toast.success(deleteSuccessToast);
    },
    onError: (err) => {
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'An unexpected error occurred';
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }) => CustomersApi.update(id, dto),
    onSuccess: () => {
      setEditingRow(null);
      queryClient.invalidateQueries({ queryKey: [entityKey] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      toast.success(updateSuccessToast);
    },
    onError: (err) => {
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'An unexpected error occurred';
      toast.error(message);
    },
  });

  const createMutation = useMutation({
    mutationFn: (dto) => CustomersApi.createForRole('SENDER', dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [entityKey] });
      toast.success('Customer created successfully');
    },
    onError: (err) => {
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'An unexpected error occurred';
      toast.error(message);
    },
  });

  const handleDelete = (id) => {
    showConfirmToast({
      message: deleteConfirmMessage,
      onConfirm: () => deleteMutation.mutate(id),
      onCancel: () => { },
      id: `delete-${entityKey}-${id}`,
    });
  };

  const handleEdit = (id) => {
    const r = rows.find((x) => x.id === id);
    if (!r) return;
    setEditingRow(r);
  };

  const handleUpdateSubmit = (dto) => {
    if (!editingRow?.id) {
      toast.error('Missing entity ID.');
      return;
    }
    updateMutation.mutate({ id: editingRow.id, dto });
  };

  const handleCreateEntity = (formData) => {
    if (!formData) {
      toast.error('Cannot create empty customer');
      return;
    }

    const payload = {
      firstName: formData.name,
      lastName: formData.lastName,
      country: formData.country,
      phone: formData.phone,
    };
    createMutation.mutate(payload);
  };

  const rangeText = useMemo(() => {
    if (!totalElements) return '0–0 of 0';
    const start = page * pageSize + 1;
    const end = Math.min(start + rows.length - 1, totalElements);
    return `${start}–${end} of ${totalElements}`;
  }, [page, pageSize, rows.length, totalElements]);

  const clearFilters = () => {
    setSearchTerm('');
    if (isAdmin) setCountry('');
  };

  useEffect(() => {
    if (location.state?.quickAction === 'new-customer') {
      setAddingEntity(true);
      const { quickAction, ...rest } = location.state || {};
      navigate(location.pathname, {
        replace: true,
        state: Object.keys(rest).length ? rest : null,
      });
    }
  }, [location.pathname, location.state, navigate]);

  if (isLoading && !pageData) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Loader text="Loading…" />
      </div>
    );
  }

  return (
    <div className="">
      <div className="card border shadow rounded-4 mb-3 sticky-top">
        <div className="card-body sticky-top w-100 p-3" style={{ top: "var(--app-sticky-offset)" }}>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 lh-1">
            <div className="d-flex align-items-center gap-2">
              <button
                className={`btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2 ${isDark ? 'text-white' : ''}`}
                onClick={() => setShowFilters((s) => !s)}
                aria-expanded={showFilters}
                aria-controls="customer-filters"
                disabled={isFetching}
              >
                <i className="bi bi-funnel" />
                {showFilters ? 'Hide filters' : 'Show filters'}
              </button>
            </div>

            <div className="d-flex align-items-center gap-2">
              {isAdmin && (
                <button
                  className={`btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2 ${isDark ? 'text-white' : ''}`}
                  title="Add"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddingEntity(true);
                  }}
                >
                  <i className="bi bi-plus-circle" aria-hidden="true" />
                  <span>Add New Customer</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {(q || effectiveCountry) && (
          <div className="card-body pt-0">
            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
              <span className="badge bg-body-tertiary border text-body">Total: {totalElements}</span>
              <small className="text-body-secondary me-1">Filters:</small>
              {q && <span className="badge bg-body-tertiary border text-body">Search: “{q}”</span>}
              {effectiveCountry && (
                <span className="badge bg-body-tertiary border text-body">
                  Country: {effectiveCountry} {!isAdmin ? '(locked)' : ''}
                </span>
              )}
              <button className="btn btn-sm btn-link text-decoration-none" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          </div>
        )}

        {showFilters && (
          <div className="card-body pt-0" id="customer-filters">
            <Filters
              data={rows}
              getCountry={getCountry}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedCountry={effectiveCountry}
              onCountryChange={isAdmin ? setCountry : () => { }}
            />
          </div>
        )}
      </div>

      <div className="card-body p-2 p-md-4 card border shadow-sm rounded-4">
        {isError && (
          <div className="alert alert-danger" role="alert">
            Failed to load customers. {error?.message || ''}
          </div>
        )}

        <EntityListView
          data={rows}
          getCountry={getCountry}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emptyMessage={emptyMessage}
          totalCount={totalElements}
          isLoading={isFetching && !!pageData}
          base="customers"
        />

        <div className="d-flex w-100 justify-content-between align-items-center mt-3">
          <small className="text-body-secondary">{rangeText}</small>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill"
              disabled={page === 0 || isFetching}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span className="text-body-secondary small">
              Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
              {isFetching ? ' …' : ''}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill"
              disabled={page + 1 >= totalPages || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editingRow && (
        <EditEntityModal
          title={`${editTitle} ${getEditLabel(editingRow)}`}
          row={editingRow}
          isBusy={updateMutation.isPending}
          onClose={() => setEditingRow(null)}
          onSubmit={handleUpdateSubmit}
        />
      )}

      {addingEntity && (
        <AddEntityModal
          lockedCountryName={effectiveCountry || ""}
          defaultCountryName={effectiveCountry || "Sweden"}
          onClose={() => setAddingEntity(false)}
          onSubmit={(data) => {
            handleCreateEntity(data);
            setAddingEntity(false);
          }}
          submitLabel="Save"
        />
      )}
    </div>
  );
}
